import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrayFieldTemplateProps, FormContextType, RJSFSchema, StrictRJSFSchema } from '@rjsf/utils';
import { MarkdownText } from '../MarkdownText';

export default function CustomArrayFieldTemplate<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any
>(props: ArrayFieldTemplateProps<T, S, F>) {
  const { canAdd, className, disabled, idSchema, items, onAddClick, readonly, registry, schema, title, uiSchema } = props;
  const ArrayFieldItemTemplate = registry.templates.ArrayFieldItemTemplate;
  const description = (uiSchema as any)?.['ui:description'] || schema?.description;
  const isEmpty = items.length === 0;

  return (
    <Box className={className} id={idSchema.$id} sx={{ width: '100%', mb: 1.8 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.4 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Chip size="small" variant="outlined" label={`${items.length}`} />
        </Stack>
        {canAdd && (
          <IconButton
            type="button"
            size="small"
            onClick={onAddClick}
            disabled={disabled || readonly}
            sx={{
              borderRadius: 2,
              width: 34,
              height: 34,
              border: '1px solid #c2d6ef',
              color: '#1f6fd6',
              backgroundColor: '#f6faff',
              '& svg': { fontSize: 20 }
            }}
            aria-label="Add entry"
            title="Add entry"
          >
            <AddIcon />
          </IconButton>
        )}
      </Stack>

      {description && (
        <Box
          sx={{
            mt: 0,
            mb: 3.1,
            px: 1.15,
            pt: 0,
            pb: 1.05,
            color: 'text.secondary',
            fontSize: '0.92rem',
            borderRadius: 1.25,
            '& p': { my: 0 },
            '& span': { lineHeight: 1.7 }
          }}
        >
          {typeof description === 'string' ? <MarkdownText>{description}</MarkdownText> : description}
        </Box>
      )}

      {isEmpty && (
        <>
          <Box
            sx={{
              px: 1.5,
              py: 1.2,
              border: '1px dashed #cbd4e1',
              borderRadius: 2,
              backgroundColor: '#fbfdff',
              color: '#5d6b82',
              fontSize: '0.9rem'
            }}
          >
            No entries yet. Use + to create the first item.
          </Box>
          <Box sx={{ height: 20 }} />
        </>
      )}

      <Stack spacing={1.4} useFlexGap>
        {items.map((item) => (
          <ArrayFieldItemTemplate {...item} />
        ))}
      </Stack>
    </Box>
  );
}
